import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../Utils/AxiosInstance";
import { revertAll } from "../ResetStateAction";
import GetQueryParams from "../../../Utils/GetQueryParams";
import { TABLE_CACHE_EXPIRY_MS } from "../../../config/cacheConfig";

const initialState = {
  Persons: [],
  PersonsPopulate: [],
  SinglePersons: [],
  organizationData: [],
  isSuccess: false,
  message: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  IsLogin: true,

  tableCache: {
    persons: {},
    cacheExpiry: TABLE_CACHE_EXPIRY_MS,
    activeCacheKey: null,
  },
};

const generatePersonsCacheKey = (type, queryParams = {}) => {
  const normalizedParams = {
    type: type || "Person",
    PageNumber: queryParams?.PageNumber || 1,
    PageSize: queryParams?.PageSize || 50,
    name: queryParams?.name || "",
    email: queryParams?.email || "",
    contactNumber: queryParams?.contactNumber || "",
    tagId: queryParams?.tagId || "",
    organizationId: queryParams?.organizationId || "",
    city: queryParams?.city || "",
    search: queryParams?.search || "",
    sortBy: queryParams?.sortBy || "CreatedAt",
    sortOrder: queryParams?.sortOrder || "desc",
  };

  const sortedParams = Object.keys(normalizedParams)
    .sort()
    .reduce((acc, key) => {
      acc[key] = normalizedParams[key];
      return acc;
    }, {});

  const queryString = JSON.stringify(sortedParams);
  return `persons_${queryString}`;
};

const isCacheValid = (cachedEntry, cacheExpiry) => {
  if (!cachedEntry || !cachedEntry.timestamp) {
    return false;
  }
  const currentTime = Date.now();
  const cacheAge = currentTime - cachedEntry.timestamp;
  return cacheAge < cacheExpiry;
};

export const fetchPersons = createAsyncThunk("/Person", async (payload, { getState, dispatch }) => {
  const { type, queryParams = {} } = payload;
  const state = getState();
  const personsSlice = state.Persons;

  const cacheKey = generatePersonsCacheKey(type, queryParams);

  // console.log("Cache check:", {
  //   cacheKey,
  //   hasCache: !!personsSlice.tableCache.persons[cacheKey],
  //   isCacheValid: personsSlice.tableCache.persons[cacheKey]
  //     ? isCacheValid(personsSlice.tableCache.persons[cacheKey], personsSlice.tableCache.cacheExpiry)
  //     : false,
  // });
  if (cacheKey && personsSlice.tableCache.persons[cacheKey]) {
    const cachedData = personsSlice.tableCache.persons[cacheKey];

    if (isCacheValid(cachedData, personsSlice.tableCache.cacheExpiry)) {
      dispatch(setActivePersonsCacheKey(cacheKey));

      return {
        ...cachedData.data,
        fromCache: true,
        cacheKey: cacheKey,
      };
    }
  }

  const queryString = GetQueryParams(queryParams);

  try {
    const response = await axiosInstance.get(`/Person?Type=${type}&${queryString}`);

    const result = {
      ...response.data,
      fromCache: false,
      cacheKey: cacheKey,
    };

    if (response.data?.Succeeded && cacheKey) {
      dispatch(
        updatePersonsTableCache({
          key: cacheKey,
          data: response.data,
        })
      );
    }

    return result;
  } catch (error) {
    console.error("❌ Error fetching persons:", error);
    console.error("📞 API endpoint called:", `/Person?Type=${type}&${queryString}`);
    throw error;
  }
});

export const fetchAllPersons = createAsyncThunk("/Person/all", async () => {
  const response = await axiosInstance.get("/Person/all");
  return response.data;
});

export const fetchPersonsPopulate = createAsyncThunk("/Person/populate", async tag => {
  const response = await axiosInstance.get(`/Person/populate?tagType=${tag}`);
  return response.data;
});

export const fetchSinglePersons = createAsyncThunk("/Person/{id}", async id => {
  const response = await axiosInstance.get(`/Person/${id}`);
  return response.data;
});

const PersonSlice = createSlice({
  name: "Persons",
  initialState,
  reducers: {
    PersonsAdded: {
      reducer(state, action) {
        state.Persons?.push(action.payload);
      },
    },

    updatePersonsTableCache: (state, action) => {
      const { key, data } = action.payload;

      if (!key || !data) {
        console.warn("⚠️ updatePersonsTableCache called with invalid key or data");
        return;
      }

      const newCacheEntry = {
        data: { ...data },
        timestamp: Date.now(),
        totalRecords: data.TotalRecords || 0,
      };

      state.tableCache.persons = {
        ...state.tableCache.persons,
        [key]: newCacheEntry,
      };

      state.tableCache.activeCacheKey = key;
    },

    invalidatePersonsCache: (state, action) => {
      const cacheKey = action.payload;

      if (cacheKey) {
        delete state.tableCache.persons[cacheKey];

        if (state.tableCache.activeCacheKey === cacheKey) {
          state.tableCache.activeCacheKey = null;
        }
      } else {
        state.tableCache.persons = {};
        state.tableCache.activeCacheKey = null;
      }
    },

    invalidatePersonsByType: (state, action) => {
      const { type } = action.payload;

      if (type) {
        Object.keys(state.tableCache.persons).forEach(key => {
          if (key.includes(`"type":"${type}"`)) {
            delete state.tableCache.persons[key];
          }
        });

        if (state.tableCache.activeCacheKey?.includes(`"type":"${type}"`)) {
          state.tableCache.activeCacheKey = null;
        }
      }
    },
    clearAllPersonsCache: state => {
      state.tableCache.persons = {};
      state.tableCache.activeCacheKey = null;
    },
    setActivePersonsCacheKey: (state, action) => {
      state.tableCache.activeCacheKey = action.payload;
    },
    setPersonsCacheExpiry: (state, action) => {
      state.tableCache.cacheExpiry = action.payload;
    },
    updatePersonInCache: (state, action) => {
      const { personId, updatedData } = action.payload;
      const cacheKey = state.tableCache.activeCacheKey;
      if (!cacheKey) return;
      const cachedData = state.tableCache.persons[cacheKey];
      if (cachedData?.data?.data) {
        const updatedPersons = cachedData.data.data.map(person =>
          person.Id === personId ? { ...person, ...updatedData } : person
        );
        cachedData.data.data = updatedPersons;
        cachedData.timestamp = Date.now();
      }
    },

    addPersonToCache: (state, action) => {
      const { person } = action.payload;
      const cacheKey = state.tableCache.activeCacheKey;
      if (!cacheKey || !person) return;
      const cachedData = state.tableCache.persons[cacheKey];
      if (cachedData?.data?.data) {
        cachedData.data.data.unshift(person);
        cachedData.data.TotalRecords = (cachedData.data.TotalRecords || 0) + 1;
        cachedData.timestamp = Date.now();
      }
    },

    removePersonFromCache: (state, action) => {
      const { personId } = action.payload;
      const cacheKey = state.tableCache.activeCacheKey;
      if (!cacheKey) return;
      const cachedData = state.tableCache.persons[cacheKey];
      if (cachedData?.data?.data) {
        cachedData.data.data = cachedData.data.data.filter(person => person.Id !== personId);
        cachedData.data.TotalRecords = Math.max(0, (cachedData.data.TotalRecords || 1) - 1);
        cachedData.timestamp = Date.now();
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(revertAll, () => initialState)
      .addCase(fetchPersons.pending, state => {
        state.status = "loading";
      })
      .addCase(fetchPersons.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload.Message;
        state.isSuccess = action.payload.Succeeded;
        const { fromCache, cacheKey, ...apiData } = action.payload;
        state.Persons = apiData;
        if (cacheKey && !fromCache) {
          state.tableCache.activeCacheKey = cacheKey;
        }
      })
      .addCase(fetchPersons.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchAllPersons.pending, state => {
        state.status = "loading";
      })
      .addCase(fetchAllPersons.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload?.Message;
        state.isSuccess = action.payload?.Succeeded ?? action.payload?.IsSuccess;
        state.Persons = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
      })
      .addCase(fetchAllPersons.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchSinglePersons.pending, state => {
        state.status = "loading";
      })
      .addCase(fetchSinglePersons.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload.Message;
        state.isSuccess = action.payload.IsSuccess;
        state.SinglePersons = action.payload.Data;
      })
      .addCase(fetchSinglePersons.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchPersonsPopulate.pending, state => {
        state.status = "loading";
      })
      .addCase(fetchPersonsPopulate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload.Message;
        state.isSuccess = action.payload.IsSuccess;
        state.PersonsPopulate = action.payload;
      })
      .addCase(fetchPersonsPopulate.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      });
  },
});

export const selectPersonsData = (state, type, queryParams = {}) => {
  const cacheKey = generatePersonsCacheKey(type, queryParams);
  const activeKey = state.Persons.tableCache.activeCacheKey;

  let personsData = [];
  let totalRecords = 0;
  let pageNumber = 1;
  let pageSize = 50;

  if (cacheKey && state.Persons.tableCache.persons[cacheKey]) {
    const cached = state.Persons.tableCache.persons[cacheKey];
    personsData = cached.data?.data || [];
    totalRecords = cached.totalRecords || 0;
    pageNumber = cached.data?.PageNumber || 1;
    pageSize = cached.data?.PageSize || 50;
  } else if (activeKey && state.Persons.tableCache.persons[activeKey]) {
    const cached = state.Persons.tableCache.persons[activeKey];
    personsData = cached.data?.data || [];
    totalRecords = cached.totalRecords || 0;
    pageNumber = cached.data?.PageNumber || 1;
    pageSize = cached.data?.PageSize || 50;
  } else {
    personsData = state.Persons.Persons?.data || [];
    totalRecords = state.Persons.Persons?.TotalRecords || 0;
    pageNumber = state.Persons.Persons?.PageNumber || 1;
    pageSize = state.Persons.Persons?.PageSize || 50;
  }

  return {
    data: personsData,
    totalRecords,
    pageNumber,
    pageSize,
    totalPages: state.Persons.Persons?.TotalPages || 0,
    fromCache: state.Persons.Persons?.fromCache || false,
  };
};

export const selectIsPersonsFromCache = state => state.Persons.Persons?.fromCache || false;

export const selectActivePersonsCacheKey = state => state.Persons.tableCache.activeCacheKey;

export const selectPersonsCacheStats = state => {
  const personsCache = state.Persons.tableCache.persons;
  const keys = Object.keys(personsCache);

  return {
    totalCachedQueries: keys.length,
    cacheExpiry: state.Persons.tableCache.cacheExpiry,
    activeKey: state.Persons.tableCache.activeCacheKey,
  };
};

export const selectHasPersonsCacheForQuery = (state, type, queryParams) => {
  const cacheKey = generatePersonsCacheKey(type, queryParams);
  if (!cacheKey) return false;

  const cachedData = state.Persons.tableCache.persons[cacheKey];
  return cachedData && isCacheValid(cachedData, state.Persons.tableCache.cacheExpiry);
};

export const getPersonsDetails = state => state.Persons;

export const {
  PersonsAdded,
  updatePersonsTableCache,
  invalidatePersonsCache,
  invalidatePersonsByType,
  clearAllPersonsCache,
  setActivePersonsCacheKey,
  setPersonsCacheExpiry,
  updatePersonInCache,
  addPersonToCache,
  removePersonFromCache,
} = PersonSlice.actions;

export { generatePersonsCacheKey, isCacheValid };

export default PersonSlice.reducer;
